import React, {useState, useEffect} from 'react';
import '../styles/EditProjectReportModal.css';
import { useToast } from '../context/UIContext';
import { formatHhmm, parseHhmm, spanMinutes, totalProjectMinutes } from '../utils/reportTime';

// Canonical location values — must match the report/DB spelling so the
// Location Overview in the PDF categorises them correctly.
const LOCATIONS = ['On-Site', 'Home Office', 'Office'];

// Normalise legacy / variant spellings (OnSite, HomeOffice, Remote) to canonical.
const normalizeLocation = (loc) => {
    const s = (loc || '').trim().toLowerCase().replace(/[-\s]/g, '');
    if (s === 'onsite') return 'On-Site';
    if (s === 'office') return 'Office';
    if (s === 'homeoffice' || s === 'remote') return 'Home Office';
    return loc || 'On-Site';
};

const blankEntry = (userId) => {
    const row = {
        date: new Date().toISOString().split('T')[0],
        startTime: '08:00',
        endTime: '17:00',
        breakMinutes: 30,
        travelMinutes: 0,
        activity: '',
        comment: '',
        ticketTitle: '',
        location: 'On-Site',
        userId,
    };
    // A fresh row books the whole day to the project — derived from the row itself so
    // the default can never drift away from the times above.
    return {...row, projectTime: formatHhmm(spanMinutes(row))};
};

// Project time of a row as it enters the editor. An older report is seeded from the day span —
// what the backend used to compute — so recalling one changes nothing until it is edited.
const initialProjectTime = (entry) => (
    parseHhmm(entry.projectTime) !== null
        ? String(entry.projectTime).trim()
        : formatHhmm(spanMinutes(entry))
);

export default function CustomReportModal({users, dateRange, tickets = [], onClose, onSubmit, onSave, initialName = ''}) {

    const toast = useToast();

    const [allEntries, setAllEntries] = useState({});
    const [reportName, setReportName] = useState(initialName);

    // Entries per user; the row order is preserved as-is.
    useEffect(() => {
        const initialEntries = {};
        users.forEach(user => {
            initialEntries[user.userId] = (user.entries || []).map(e => ({
                ...e,
                location: normalizeLocation(e.location),
                ticketTitle: e.ticketTitle || e.ticket_title || '',
                projectTime: initialProjectTime(e),
            }));
        });
        setAllEntries(initialEntries);
    }, [users]);

    const handleEntryChange = (userId, index, field, value) => {
        if (field === 'date' && value && dateRange?.startDate && dateRange?.endDate) {
            const d = new Date(value);
            if (d < new Date(dateRange.startDate) || d > new Date(dateRange.endDate)) {
                toast(`Datum außerhalb des erlaubten Bereichs: ${dateRange.startDate} bis ${dateRange.endDate}`);
                return;
            }
        }
        setAllEntries(prev => {
            const list = [...(prev[userId] || [])];
            list[index] = {...list[index], [field]: value};
            return {...prev, [userId]: list};
        });
    };

    // Insert a blank row at an exact position (order is NOT auto-sorted).
    const handleInsertEntry = (userId, atIndex) => {
        setAllEntries(prev => {
            const list = [...(prev[userId] || [])];
            list.splice(atIndex, 0, blankEntry(userId));
            return {...prev, [userId]: list};
        });
    };

    const handleDeleteEntry = (userId, index) => {
        setAllEntries(prev => {
            const list = [...(prev[userId] || [])];
            list.splice(index, 1);
            return {...prev, [userId]: list};
        });
    };

    // Collect edited entries into per-user blocks, carrying the username so a
    // saved report can be recalled with names intact.
    const buildBlocks = () => {
        const nameById = {};
        users.forEach(u => { nameById[u.userId] = u.username || u.name || u.userId; });
        const result = [];
        Object.entries(allEntries).forEach(([userId, entries]) => {
            if (entries.length > 0) {
                result.push({userId, username: nameById[userId], entries});
            }
        });
        return result;
    };

    // A row whose Projektzeit is not a duration would be reported as 00:00 and quietly
    // shrink the total, so it is refused before a PDF is built or a report is stored.
    const hasInvalidProjectTime = (blocks) =>
        blocks.some(b => b.entries.some(e => parseHhmm(e.projectTime) === null));

    const handleGenerate = () => {
        const result = buildBlocks();
        if (result.length === 0) {
            toast('Keine Einträge zum Erstellen.');
            return;
        }
        if (hasInvalidProjectTime(result)) {
            toast('Bitte jede Projektzeit im Format HH:MM angeben.');
            return;
        }
        onSubmit(result);
        onClose();
    };

    const handleSave = () => {
        if (!onSave) return;
        if (!reportName.trim()) {
            toast('Bitte einen Namen für den Bericht angeben.');
            return;
        }
        const result = buildBlocks();
        if (result.length === 0) {
            toast('Keine Einträge zum Speichern.');
            return;
        }
        if (hasInvalidProjectTime(result)) {
            toast('Bitte jede Projektzeit im Format HH:MM angeben.');
            return;
        }
        onSave({name: reportName.trim(), users: result});
    };

    const ticketTitles = Array.from(new Set((tickets || []).map(t => t.title).filter(Boolean)));

    return (
        <div className="modal-report-overlay">
            <div className="modal-report crm">

                <div className="crm-header">
                    <h3 className="crm-title">Einträge bearbeiten</h3>
                    {onSave && (
                        <input
                            type="text"
                            className="crm-name"
                            value={reportName}
                            onChange={e => setReportName(e.target.value)}
                            placeholder="Berichtsname (z.B. Infineon Juli 2026)"
                        />
                    )}
                    <div className="crm-actions">
                        <button onClick={handleGenerate} className="crm-btn crm-btn--primary">
                            Bericht generieren
                        </button>
                        {onSave && (
                            <button onClick={handleSave} className="crm-btn">Speichern</button>
                        )}
                        <button onClick={onClose} className="crm-btn crm-btn--danger">Abbrechen</button>
                    </div>
                </div>

                <div className="crm-body">
                    {users.map(user => {
                        const entries = allEntries[user.userId] || [];
                        // The sum the report will print — visible while editing, so an
                        // inflated total shows up here and not first in the PDF.
                        const total = formatHhmm(totalProjectMinutes(entries));
                        return (
                            <div key={user.userId} className="crm-user">
                                <div className="crm-user-head">
                                    <h4 className="crm-user-name">{user.username || user.name}</h4>
                                    <span className="crm-user-total">
                                        Summe Projektzeit: <strong>{total}</strong>
                                    </span>
                                    <button
                                        className="crm-btn crm-btn--ghost"
                                        onClick={() => handleInsertEntry(user.userId, entries.length)}
                                    >
                                        + Zeile hinzufügen
                                    </button>
                                </div>

                                <div className="crm-table-wrap">
                                    <table className="crm-table">
                                        <thead>
                                        <tr>
                                            <th style={{width: '44px'}}></th>
                                            <th>Datum</th>
                                            <th title="Zeit, die auf dieses Projekt gebucht wird (HH:MM). Sie erscheint im PDF und bildet die Summe.">
                                                Projektzeit
                                            </th>
                                            <th title="Beginn des Arbeitstags — Kontext für den Bericht, nicht die Projektzeit.">Start</th>
                                            <th title="Ende des Arbeitstags — Kontext für den Bericht, nicht die Projektzeit.">Ende</th>
                                            <th>Pause</th>
                                            <th>Fahrtzeit</th>
                                            <th>Ticket</th>
                                            <th>Einsatzort</th>
                                            <th>Aktivität</th>
                                            <th>Kommentar</th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        {entries.length === 0 && (
                                            <tr>
                                                <td colSpan={11} className="crm-empty">
                                                    Keine Einträge — „+ Zeile hinzufügen" verwenden.
                                                </td>
                                            </tr>
                                        )}
                                        {entries.map((entry, index) => (
                                            <tr key={index}>
                                                <td>
                                                    <div className="crm-row-actions">
                                                        <button
                                                            className="crm-icon"
                                                            title="Zeile darüber einfügen"
                                                            onClick={() => handleInsertEntry(user.userId, index)}
                                                        >
                                                            +
                                                        </button>
                                                        <button
                                                            className="crm-icon crm-icon--danger"
                                                            title="Zeile löschen"
                                                            onClick={() => handleDeleteEntry(user.userId, index)}
                                                        >
                                                            ✕
                                                        </button>
                                                    </div>
                                                </td>
                                                <td>
                                                    <input type="date" className="crm-in"
                                                           value={entry.date || ''}
                                                           onChange={e => handleEntryChange(user.userId, index, 'date', e.target.value)}/>
                                                </td>
                                                <td>
                                                    {/* Typed directly rather than expressed through
                                                        Start/Ende, which repeat per ticket row. */}
                                                    <input type="text" aria-label="Projektzeit" placeholder="HH:MM"
                                                           className={`crm-in crm-in--sm${parseHhmm(entry.projectTime) === null ? ' crm-in--error' : ''}`}
                                                           value={entry.projectTime ?? ''}
                                                           onChange={e => handleEntryChange(user.userId, index, 'projectTime', e.target.value)}/>
                                                </td>
                                                <td>
                                                    <input type="time" className="crm-in"
                                                           value={entry.startTime || ''}
                                                           onChange={e => handleEntryChange(user.userId, index, 'startTime', e.target.value)}/>
                                                </td>
                                                <td>
                                                    <input type="time" className="crm-in"
                                                           value={entry.endTime || ''}
                                                           onChange={e => handleEntryChange(user.userId, index, 'endTime', e.target.value)}/>
                                                </td>
                                                <td>
                                                    <input type="number" min="0" className="crm-in crm-in--sm"
                                                           value={entry.breakMinutes ?? 0}
                                                           onChange={e => handleEntryChange(user.userId, index, 'breakMinutes', e.target.value)}/>
                                                </td>
                                                <td>
                                                    <input type="number" min="0" className="crm-in crm-in--sm"
                                                           value={entry.travelMinutes ?? 0}
                                                           onChange={e => handleEntryChange(user.userId, index, 'travelMinutes', e.target.value)}/>
                                                </td>
                                                <td>
                                                    <select className="crm-in"
                                                            value={entry.ticketTitle || ''}
                                                            onChange={e => handleEntryChange(user.userId, index, 'ticketTitle', e.target.value)}>
                                                        <option value="">— kein Ticket —</option>
                                                        {ticketTitles.map(t => (
                                                            <option key={t} value={t}>{t}</option>
                                                        ))}
                                                        {entry.ticketTitle && !ticketTitles.includes(entry.ticketTitle) && (
                                                            <option value={entry.ticketTitle}>{entry.ticketTitle}</option>
                                                        )}
                                                    </select>
                                                </td>
                                                <td>
                                                    <select className="crm-in"
                                                            value={entry.location || 'On-Site'}
                                                            onChange={e => handleEntryChange(user.userId, index, 'location', e.target.value)}>
                                                        {LOCATIONS.map(loc => (
                                                            <option key={loc} value={loc}>{loc}</option>
                                                        ))}
                                                        {entry.location && !LOCATIONS.includes(entry.location) && (
                                                            <option value={entry.location}>{entry.location}</option>
                                                        )}
                                                    </select>
                                                </td>
                                                <td>
                                                    <input type="text" className="crm-in"
                                                           value={entry.activity || ''}
                                                           onChange={e => handleEntryChange(user.userId, index, 'activity', e.target.value)}/>
                                                </td>
                                                <td>
                                                    <input type="text" className="crm-in"
                                                           value={entry.comment || ''}
                                                           onChange={e => handleEntryChange(user.userId, index, 'comment', e.target.value)}/>
                                                </td>
                                            </tr>
                                        ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}
